// ENT-5715 (folds ENT-5717): a fetch that a full-page navigation cancels is not a read failure.
//
// Chromium rejects an in-flight fetch with `TypeError: Failed to fetch` when the document
// commits a navigation away. It does so AFTER `pagehide` has fired, and the leaving document
// still runs the rejection handler (verified in headless Chromium with the next document
// arriving after 0, 300 and 1,500 ms, 2026-10-18). That message is identical to a real network
// failure, so the deterministic discriminator is whether this document is being unloaded.

let pageUnloading = false;

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => {
    pageUnloading = true;
  });
  // A back/forward-cache restore brings the same document back to life.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) pageUnloading = false;
  });
}

/** True between this document's `pagehide` and a back/forward-cache restore. */
export function isPageUnloading(): boolean {
  return pageUnloading;
}

/**
 * A rejected fetch's exact message in Chromium, Firefox and WebKit. postgrest-js reports the
 * rejection as `{ message: '<name>: <message>', code: '' }`, hence the optional prefix. Anchored:
 * a backend message that merely contains these words is not a transport failure.
 */
const TRANSPORT_FAILURE = /^(?:TypeError: )?(?:Failed to fetch|NetworkError when attempting to fetch resource\.?|Load failed)$/;
/** postgrest-js's shape for an aborted fetch (`AbortError: signal is aborted without reason`). */
const PREFIXED_ABORT = /^AbortError: /;

function isCancelledMessage(message: unknown): boolean {
  return typeof message === 'string' && (TRANSPORT_FAILURE.test(message) || PREFIXED_ABORT.test(message));
}

/**
 * True for a concrete browser cancellation only: an `AbortError`, or a transport-failure message,
 * possibly wrapped as a `cause`. An error carrying a string `code` (a SQLSTATE or an API error
 * code) is a server response and never matches, whatever its message says.
 */
function isBrowserCancelledRequest(error: unknown, depth = 0): boolean {
  if (typeof error === 'string') return isCancelledMessage(error);
  if (!error || typeof error !== 'object' || depth > 3) return false;
  const { name, message, code, cause } = error as { name?: unknown; message?: unknown; code?: unknown; cause?: unknown };
  if (typeof code === 'string' && code !== '') return false;
  if (name === 'AbortError' || isCancelledMessage(message)) return true;
  return isBrowserCancelledRequest(cause, depth + 1);
}

/**
 * True only for a request the browser cancelled because this page is navigating away: the
 * document is unloading AND the error is a concrete transport failure or abort. Every other error,
 * including a backend error whose message mentions an abort, and every transport failure on a live
 * page, is a real read failure the caller still reports.
 */
export function isNavigationAbortedFetch(error: unknown): boolean {
  return pageUnloading && isBrowserCancelledRequest(error);
}
